import type { QRFormValues } from "../schemas/qr-schemas";

const SOCIAL_URLS = {
  x: "https://x.com/",
  instagram: "https://instagram.com/",
  facebook: "https://facebook.com/",
  linkedin: "https://linkedin.com/in/",
  github: "https://github.com/",
  tiktok: "https://tiktok.com/@",
  youtube: "https://youtube.com/@",
  twitch: "https://twitch.tv/",
  bluesky: "https://bsky.app/profile/"
} as const;

const escapeText = (value = "") => value
  .replace(/\\/g, "\\\\")
  .replace(/\n/g, "\\n")
  .replace(/,/g, "\\,")
  .replace(/;/g, "\\;");

const escapeWifi = (value = "") => value.replace(/[\\;,":]/g, "\\$&");

const formatEventDate = (value: string) => {
  const compact = value.replace(/[-:]/g, "");
  return compact.length === 13 ? `${compact}00` : compact;
};

const withQuery = (base: string, values: Record<string, string | undefined>) => {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  return query.size ? `${base}?${query}` : base;
};

export function getQRCodeValue(values: QRFormValues): string {
  switch (values.type) {
    case "url":
      return values.url;
    case "vcard":
      return `BEGIN:VCARD\r
VERSION:3.0\r
N:${escapeText(values.lastName)};${escapeText(values.firstName)};;;\r
FN:${escapeText(`${values.firstName} ${values.lastName}`.trim())}\r
TEL:${escapeText(values.phone)}\r
EMAIL:${escapeText(values.email)}${values.organization ? `\r\nORG:${escapeText(values.organization)}` : ""}${
        values.street || values.city || values.state || values.zip || values.country
          ? `\r\nADR:;;${escapeText(values.street)};${escapeText(values.city)};${escapeText(values.state)};${
              escapeText(values.zip)
            };${escapeText(values.country)}`
          : ""
      }
END:VCARD`;
    case "phone":
      return `tel:${values.phone}`;
    case "sms":
      return withQuery(`sms:${values.phone}`, { body: values.message });
    case "email":
      return withQuery(`mailto:${values.email}`, { subject: values.subject, body: values.body });
    case "wifi":
      return `WIFI:T:${values.encryption};S:${escapeWifi(values.ssid)};P:${escapeWifi(values.password)};;`;
    case "crypto":
      return withQuery(`${values.cryptoType}:${values.address}`, { amount: values.amount, message: values.message });
    case "geo":
      return `geo:${values.latitude},${values.longitude}`;
    case "event":
      return `BEGIN:VCALENDAR\r
VERSION:2.0\r
BEGIN:VEVENT\r
SUMMARY:${escapeText(values.summary)}\r
DTSTART:${formatEventDate(values.startDate)}\r
DTEND:${formatEventDate(values.endDate)}${values.description ? `\r\nDESCRIPTION:${escapeText(values.description)}` : ""}\r
END:VEVENT\r
END:VCALENDAR`;
    case "text":
      return values.text;
    case "mecard":
      return `MECARD:N:${escapeText(values.name)};TEL:${escapeText(values.phone)}${
        values.email ? `;EMAIL:${escapeText(values.email)}` : ""
      };;`;
    case "social":
      return SOCIAL_URLS[values.platform] + values.username;
    default:
      return "";
  }
}
